import { useEffect, useRef, useState } from 'react'
import store from '../../../store'
import { selectActiveChartId, selectChartFocused, selectChartReplayMode } from '../../../selectors/chart'
import { selectActiveLayoutId } from '../../../selectors/home'
import { WidgetTypes } from '../../../components/widgetDashboard/widgetDashboard.enums'

export type StockBarRuntime = {
    activeChartId: string
    disableChartOptions: boolean
    disableSettingsButton: boolean
    disableAlertButton: boolean
}

function computeRuntime(ivPlotTimeBucket: boolean): StockBarRuntime {
    const state = store.getState()
    const activeChartId = selectActiveChartId(state)
    const chartFocused = selectChartFocused(state)
    const replayModeState = selectChartReplayMode(state)
    const activeLayoutId = selectActiveLayoutId(state)
    const dashboard = state.widgetDashboardStore.layouts[activeLayoutId]
    const activeWidgetType = dashboard?.activeWidget
        ? dashboard.widgets[dashboard.activeWidget]?.widgetType
        : undefined

    const isRestrictedWidget =
        activeWidgetType != null &&
        [WidgetTypes.MULTI_STRIKE_CHART, WidgetTypes.OI_CHART, WidgetTypes.OPEN_INTEREST_CHART, WidgetTypes.OPTION_CHAIN].includes(activeWidgetType)

    const hasActiveWidget = activeWidgetType != null

    return {
        activeChartId,
        disableChartOptions: !!replayModeState || isRestrictedWidget || !chartFocused,
        disableSettingsButton: !!replayModeState || (!chartFocused && !hasActiveWidget),
        disableAlertButton: !!replayModeState || isRestrictedWidget || ivPlotTimeBucket || !activeChartId
    }
}

export function useStockBarRuntime(ivPlotTimeBucket: boolean): StockBarRuntime {
    const [runtime, setRuntime] = useState(() => computeRuntime(ivPlotTimeBucket))
    const runtimeRef = useRef(runtime)

    useEffect(() => {
        const sync = () => {
            const next = computeRuntime(ivPlotTimeBucket)
            const prev = runtimeRef.current
            // Never call setState with an unchanged value from a store subscription: React keeps every
            // such no-op update queued on the hook until the component next renders, and with ticks
            // arriving ~10 times a second these queues grew by tens of MB per hour.
            if (
                prev.activeChartId === next.activeChartId &&
                prev.disableChartOptions === next.disableChartOptions &&
                prev.disableSettingsButton === next.disableSettingsButton &&
                prev.disableAlertButton === next.disableAlertButton
            ) {
                return
            }
            runtimeRef.current = next
            setRuntime(next)
        }
        sync()
        return store.subscribe(sync)
    }, [ivPlotTimeBucket])

    return runtime
}
